/**
 * Turning library files into playlist items, and showing them back.
 *
 * A {@link SignageItem} stores only an asset URL, so everything an editor wants
 * to display - a thumbnail, whether it is a clip, how long that clip runs - has
 * to be recovered by looking the URL back up in the media library. These are
 * the lookups, kept out of the components so they can be tested directly.
 */
import type { AssetSummary, SignageScopeRef } from "../http/foyer";
import type {
  SignageItem,
  SignageItemKind,
  SignageScope,
} from "@shared/types/signage";

/**
 * The layer fields every signage record carries.
 *
 * Playlists and day plans are owned the same way, so the ownership questions
 * below are asked of this rather than of one record type.
 */
interface SignageOwned {
  scope: SignageScope;
  premiseId: string | null;
  companyId: string | null;
}

/**
 * Fallback when a URL is not in the library.
 *
 * An item can outlive the file it points at, or point into a layer the current
 * user cannot list. The extension is the only signal left, and getting it wrong
 * only mislabels a row.
 */
const VIDEO_EXTENSION = /\.(mp4|m4v|mov|webm)(\?|#|$)/i;

/** Index a library by URL, which is how items refer to files. */
export function byUrl(assets: AssetSummary[]): Map<string, AssetSummary> {
  return new Map(assets.map((a) => [a.url, a]));
}

export function kindOf(
  asset: AssetSummary | undefined,
  url: string
): SignageItemKind {
  if (asset) return asset.contentType.startsWith("video/") ? "video" : "image";
  return VIDEO_EXTENSION.test(url) ? "video" : "image";
}

/**
 * What to render as a row's thumbnail, or null to fall back to an icon.
 *
 * A clip resolves to its captured poster, NEVER to its own URL: a video URL in
 * an `<img>` renders nothing, which is the blank-square bug the media library
 * already had. A clip uploaded before posters were captured has none, and an
 * icon is the honest answer there.
 */
export function thumbnailFor(asset: AssetSummary | undefined): string | null {
  // A web-page item has no library row and therefore no thumbnail; the editor
  // renders an icon for it, the same honest fallback a poster-less clip gets.
  if (!asset) return null;
  if (asset.contentType.startsWith("video/")) return asset.posterUrl;
  return asset.url;
}

/**
 * A new WEB PAGE item at the end of a playlist.
 *
 * The counterpart of {@link itemFromAsset}, and deliberately a separate
 * function rather than a branch inside it: a page has no library row, so none
 * of that function's inputs exist. `kindOf()` cannot help either - it derives
 * the kind from a MIME type or a file extension, and a page has neither, which
 * is why a URL item can only ever be created by an explicit call like this one.
 *
 * `durationSeconds` is the playlist default rather than 0. A page has no
 * natural end to fall back on, and an operator who never opens the field should
 * still get a slot length they can predict from the playlist they are looking
 * at. `muted` is true and `fit` is "cover" only to satisfy the shape - neither
 * means anything to an iframe, and the editor does not offer them for a page.
 */
export function itemFromWebUrl(
  webUrl: string,
  seq: number,
  defaultSeconds: number
): SignageItem {
  return {
    id: `${Date.now()}-${seq}`,
    // Empty, and it must stay empty: `assetUrl` means "a file in our media
    // library" to the panel's local cache, to asset-usage counting, and to the
    // legacy locator. A page belongs in `webUrl` alone.
    assetUrl: "",
    webUrl: webUrl.trim(),
    kind: "url",
    durationSeconds: defaultSeconds,
    fit: "cover",
    muted: true,
  };
}

/**
 * A new LIVE TV CHANNEL item at the end of a playlist.
 *
 * The third factory, and like {@link itemFromWebUrl} it is separate because its
 * input is not a library row: a channel comes from the content catalogue, and
 * the item stores only its `contentAssetId`. The playable URL is resolved at
 * TIMELINE-BUILD time and never stored - storing it drifts the moment the title
 * is re-packaged, and the drift is invisible until a wall goes black.
 *
 * ## Duration means what it means for any other item
 *
 * A channel BLOCK on a day plan has no duration - the block's time range is how
 * long it holds the screen. A channel ITEM inside a loop is different and
 * deliberately so: it is one entry in a rotation, and "hold the screen this
 * long" is exactly what the loop needs to know to move on to the next slide.
 * Which is why it takes the playlist default rather than 0.
 *
 * `kind` is `"video"`, not a kind of its own. What arrives at the panel is a
 * manifest, and every branch that matters downstream - AVPlay routing, Shaka,
 * the legacy facade's asset type - already asks that question correctly. A
 * fourth kind would mean revisiting all of them to answer the same way.
 */
export function itemFromChannel(
  channel: { contentAssetId: string },
  seq: number,
  defaultSeconds: number
): SignageItem {
  return {
    id: `${Date.now()}-${seq}`,
    // Resolved at build time from `contentAssetId`; see `materialiseContent`.
    assetUrl: "",
    contentAssetId: channel.contentAssetId,
    kind: "video",
    durationSeconds: defaultSeconds,
    fit: "cover",
    // A lobby is a shared room. Sound is opt-in per item, and a channel that
    // starts talking over a reception desk is the one thing nobody asked for.
    muted: true,
  };
}

/**
 * A new VENUE-CONTENT item at the end of a channel.
 *
 * The fifth factory, and separate for the same reason as the others: its input
 * is a catalogue asset rather than a library row, a URL or a template. Like a
 * live channel it stores only the `contentAssetId` - the playable URL is
 * resolved at TIMELINE-BUILD time, because a stored one drifts the moment the
 * title is re-packaged and the drift is invisible until a wall goes black.
 *
 * ## Duration is ZERO, and that is the whole point
 *
 * A venue clip has a natural end and the resolver knows it; typing a number
 * that must match the file is the trap the portal already fell into, where an
 * operator's guess silently truncates or freezes the moment somebody replaces
 * the file. `0` means "play it to its end" for a video, which is what a
 * fireplace or a gym loop wants.
 */
export function itemFromEffectAsset(
  asset: { assetId: string },
  seq: number
): SignageItem {
  return {
    id: `${Date.now()}-${seq}`,
    // Resolved at build time from `contentAssetId`; see `materialiseContent`.
    assetUrl: "",
    contentAssetId: asset.assetId,
    kind: "video",
    // See the docblock: never a typed number.
    durationSeconds: 0,
    fit: "cover",
    // Venue ambience in a lobby. Sound is opt-in, per item, like everything
    // else that plays in a shared room.
    muted: true,
  };
}

/**
 * A new SLIDE item at the end of a playlist.
 *
 * The fourth factory, and separate for the same reason as the others: its input
 * is neither a library row nor a URL but a filled-in template instance, and the
 * item stores only that instance's id. The design and the values are resolved
 * into the timeline at build time, so nothing about how the slide LOOKS is
 * copied here - correcting a price in the slide corrects every playlist showing
 * it, which is the whole reason an instance is its own entity.
 */
export function itemFromSlide(
  slide: { id: string },
  seq: number,
  defaultSeconds: number
): SignageItem {
  return {
    id: `${Date.now()}-${seq}`,
    assetUrl: "",
    templateInstanceId: slide.id,
    kind: "template",
    // A slide has no natural length, so the number is entirely how long the
    // loop lends it the wall.
    durationSeconds: defaultSeconds,
    fit: "cover",
    muted: true,
  };
}

/** A new item at the end of a playlist, taking its length from the file. */
export function itemFromAsset(
  asset: AssetSummary,
  seq: number,
  defaultSeconds: number
): SignageItem {
  const kind = kindOf(asset, asset.url);
  return {
    // Unique within the playlist, which is all `id` promises. The sequence
    // number keeps two files added in the same millisecond apart.
    id: `${Date.now()}-${seq}`,
    assetUrl: asset.url,
    kind,
    /*
     * A clip carries its own measured length.
     *
     * It would be tidier to store 0 and call it "play to the end", but 0 means
     * "use the playlist default" to the resolver - and the resolver has no
     * media library, so it cannot discover the real length later. Storing 0
     * would allot 15s to a 42s clip: the screen would cut it off, and the
     * schedule would drift from what the timeline claims.
     *
     * Rounded UP so an item is never shorter than the clip inside it. A clip
     * whose length was never measured falls back to 0, which is honest - we do
     * not know, and the operator is asked.
     */
    durationSeconds:
      kind === "video"
        ? asset.durationSeconds
          ? Math.ceil(asset.durationSeconds)
          : 0
        : defaultSeconds,
    // A picture shows its FULL width by default - letterboxing is better than
    // a poster whose edges (and often its text) are cropped off. `Fill` stays
    // available per item; a video keeps filling, as before.
    fit: kind === "image" ? "contain" : "cover",
    muted: true,
  };
}

/**
 * How long one item holds the screen, for display in the editor.
 *
 * Mirrors the resolver's rule - `0` falls back to the playlist default - but
 * prefers the clip's REAL length when the library knows it. The resolver
 * cannot do that (it has no library), so this is an editor-only refinement:
 * it shows an operator the truth instead of a placeholder.
 */
export function displaySeconds(
  item: SignageItem,
  defaultSeconds: number,
  asset: AssetSummary | undefined
): number {
  if (item.durationSeconds > 0) return item.durationSeconds;
  if (asset?.durationSeconds && asset.durationSeconds > 0) {
    return Math.round(asset.durationSeconds);
  }
  return defaultSeconds;
}

/** Total loop length, using the same per-item rule. */
export function loopSeconds(
  items: SignageItem[],
  defaultSeconds: number,
  assets: Map<string, AssetSummary>
): number {
  return items.reduce(
    (sum, it) =>
      sum + displaySeconds(it, defaultSeconds, assets.get(it.assetUrl)),
    0
  );
}

/**
 * Fill in clip lengths the library knows but the item does not.
 *
 * Items saved before lengths were recorded hold `0`, which the resolver reads
 * as "use the playlist default" - so a 42s clip is allotted 15s and cut off
 * mid-play. Nothing downstream can repair that, because neither the resolver
 * nor the TV has a media library to ask. The editor does, so opening a
 * playlist quietly measures what it can; saving makes the repair stick.
 *
 * A clip whose length is still unknown keeps its `0` and is asked about.
 */
export function withMeasuredDurations(
  items: SignageItem[],
  assets: Map<string, AssetSummary>
): SignageItem[] {
  return items.map((it) => {
    if (it.kind !== "video" || it.durationSeconds > 0) return it;
    const known = assets.get(it.assetUrl)?.durationSeconds;
    if (!known || known <= 0) return it;
    return { ...it, durationSeconds: Math.ceil(known) };
  });
}

/** Move an item within a playlist, clamped at the ends. */
export function reorder<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/**
 * Whether a playlist belongs to the scope currently being edited.
 *
 * A location sees its chain's loops and Nenda's curated channels alongside its
 * own, because it can SCHEDULE all of them. It must not be able to EDIT the
 * borrowed ones: renaming a chain playlist from one hotel would change what
 * every other hotel in the chain plays, from a screen that gave no hint the
 * record was shared. Those are offered as a duplicate instead.
 */
export function isOwnedBy(
  playlist: SignageOwned,
  scope: SignageScopeRef
): boolean {
  if (scope.kind === "premise") return playlist.premiseId === scope.premiseId;
  // The platform's own layer (`BE-40`): a record with neither id set. Only an
  // admin ever authors in this scope, and there the curated channels are
  // editable rather than borrowed - the whole point of the scope existing.
  if (scope.kind === "global")
    return playlist.premiseId === null && playlist.companyId === null;
  return playlist.premiseId === null && playlist.companyId === scope.companyId;
}

/** Where a borrowed playlist came from, for a badge. */
export function ownerLabel(playlist: SignageOwned): string {
  if (playlist.scope === "global") return "Nenda channel";
  return playlist.scope === "company" ? "Company" : "This location";
}

/**
 * What to CALL the thing on screen, in a line an operator reads.
 *
 * ⚠️ This used to be `item.assetUrl.split("/").pop()`, so a wall playing a
 * carefully authored loop reported "a.jpg". A filename is an implementation
 * detail of where a file was stored; it is not the name of anything.
 *
 * A `SignageItem` carries no title of its own and deliberately so - the name
 * lives with the ASSET, and duplicating it onto every placement is the
 * denormalisation that goes stale (the `channelName` comment on
 * `SignageDayPlanBlock` says the same thing about names generally). So this
 * uses the best identifier the timeline actually carries, per kind, and only
 * falls back to a filename when there is nothing better.
 *
 * `assetName` is optional because the caller may not have the media library
 * loaded; when it does, a real title beats everything below.
 */
export function signageItemLabel(
  item: SignageItem,
  opts: {
    assetName?: (assetUrl: string) => string | undefined;
    /**
     * What the loop itself is called - used when the ITEM has no name of its
     * own and its url cannot supply one.
     *
     * A catalogue title's `assetUrl` is a packager manifest
     * (`…/__f/manifest.mpd`), so the filename fallback produced the word
     * "manifest" on the card of every venue channel. "Bar Vibes" is both true
     * and what the operator put on the wall.
     */
    channelName?: string;
  } = {}
): string {
  if (item.kind === "url" && item.webUrl) {
    try {
      // The host, not the whole URL: a tracking-laden address wraps over three
      // lines and says less than "actic.se" does.
      return new URL(item.webUrl).hostname.replace(/^www\./, "");
    } catch {
      return item.webUrl;
    }
  }
  if (item.kind === "template") return "Slide";
  const named = item.assetUrl ? opts.assetName?.(item.assetUrl) : undefined;
  if (named) return named;
  // ⚠️ Was `contentAssetId && !assetUrl`, which called every catalogue title a
  // live channel - fine while channels were the only kind, wrong the moment
  // venue VODs arrived. The resolver marks it now.
  if (item.live) return "Live channel";
  /*
   * A CATALOGUE title never gets its name from its url.
   *
   * The name lives on the asset, and when the caller could not resolve it the
   * honest answer is the loop this clip belongs to - not the last path segment
   * of a packager manifest, which is the same word for every title in the
   * library.
   */
  if (item.contentAssetId) return opts.channelName || "Catalogue title";
  return prettyFileName(item.assetUrl);
}

/** `2026-08-a1b2c3_sunset-bar.mp4` -> `sunset bar`. */
function prettyFileName(url: string): string {
  const file = url.split("/").pop()?.split("?")[0] ?? "";
  if (!file) return "Untitled";
  const stem = decodeURIComponent(file).replace(/\.[a-z0-9]{2,4}$/i, "");
  // Upload pipelines prefix a hash or a timestamp; neither is part of the name
  // anybody gave the file.
  const withoutPrefix = stem.replace(/^[0-9a-f]{8,}[-_]/i, "");
  const words = withoutPrefix.replace(/[-_]+/g, " ").trim();
  return words || stem || "Untitled";
}
